import { getScribeHttpClients } from '@heidi/api/client';

import { parseStructuredMapping, type StructuredMapping } from './structured-mapping';

/**
 * Shared placement store for the consolidated note-push flow — the module both
 * callers key on: the overlay's new warm chip (GET + POST) and the projects
 * verification panel (GET, read-only hydrate). Wraps ml-scribe
 * `GET/POST /api/v2/ml-scribe/note-push-targets` (TemplateSectionUiTargetService):
 * the learned, per-section field placements ("ui targets") that write a note's
 * sections into an EHR.
 *
 * - GET lists a template's stored targets (optionally scoped to an app). Each
 *   `ui_target` is an `AxSelector` serialized as a JSON string.
 * - POST records VERIFIED placements only. Byte-verbatim note CONTENT never
 *   crosses this endpoint (read-back verify stays on-device — PHI minimization);
 *   the server rejects a grammar violation, a non-canonical heading, or
 *   `verified !== true`.
 *
 * `workspaceId` is threaded EXPLICITLY per call (from `copilot:active-note` in
 * the overlay, from the ambient workspace atom in the panel) — never read from a
 * global here, because the overlay's shared-origin atom can be stale. A `null`
 * workspace refuses up front; it is mandatory flow identity, never a default.
 * Failures are returned, not thrown, so callers degrade to escalating the push.
 */

const NOTE_PUSH_TARGETS_PATH = '/note-push-targets';
const HEIDI_WORKSPACE_ID_HEADER = 'Heidi-Workspace-Id';
const REQUEST_TIMEOUT_MS = 15_000;

/** Contract error when note-push runs without the mandatory workspace identity. */
export const WORKSPACE_REQUIRED_ERROR =
  'note-push requires a workspace (mandatory flow identity — no fallback)';

/** One stored section→field placement. `uiTargets` are `AxSelector` JSON strings. */
export interface ComputerUseNotePushTargetRow {
  sectionHeading: string;
  uiTargets: string[];
  appName: string;
  /** Present instead of `uiTargets` on a structured row. */
  mapping?: StructuredMapping;
}

/**
 * A verified placement to record. `verified` is always `true` — the endpoint
 * records verified placements only, so an unverified one is never sent.
 */
export interface ComputerUseNotePushPlacement {
  sectionHeading: string;
  uiTargets: string[];
  verified: true;
}

/** Discriminated result so callers can branch to the agent-escalation fallback. */
export type ComputerUseNotePushOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

/** Parse the `{ rows: [...] }` envelope shared by the GET and POST responses. */
const parseRows = (payload: unknown): ComputerUseNotePushTargetRow[] | null => {
  if (!isRecord(payload) || !Array.isArray(payload.rows)) return null;
  const rows: ComputerUseNotePushTargetRow[] = [];
  for (const item of payload.rows) {
    if (!isRecord(item)) return null;
    if (typeof item.section_heading !== 'string') return null;
    // One payload per row; missing and null both read as absent.
    const hasMapping = item.mapping !== undefined && item.mapping !== null;
    const mapping = hasMapping ? parseStructuredMapping(item.mapping) : undefined;
    // One unreadable structured row is dropped alone, never failing the read.
    if (hasMapping && !mapping) continue;
    if (!mapping && !isStringArray(item.ui_targets)) return null;
    rows.push({
      sectionHeading: item.section_heading,
      uiTargets: isStringArray(item.ui_targets) ? item.ui_targets : [],
      appName: typeof item.app_name === 'string' ? item.app_name : '',
      ...(mapping ? { mapping } : {}),
    });
  }
  return rows;
};

/**
 * Run `request` under the mandatory-workspace assertion, a timeout, and a shared
 * error envelope. A `null` workspace short-circuits with {@link
 * WORKSPACE_REQUIRED_ERROR} before any HTTP call — never a header-less request
 * that lets the backend infer a workspace.
 */
const scopedRequest = async <T>(
  workspaceId: string | null,
  label: string,
  request: (workspaceId: string, signal: AbortSignal) => Promise<T>
): Promise<ComputerUseNotePushOutcome<T>> => {
  if (!workspaceId) return { ok: false, error: WORKSPACE_REQUIRED_ERROR };
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return { ok: true, value: await request(workspaceId, controller.signal) };
  } catch (e) {
    const reason = controller.signal.aborted ? 'timed out' : (e as Error).message;
    return { ok: false, error: `note-push-targets ${label} failed: ${reason}` };
  } finally {
    clearTimeout(timeout);
  }
};

const workspaceHeaders = (workspaceId: string): Record<string, string> => ({
  [HEIDI_WORKSPACE_ID_HEADER]: workspaceId,
});

/**
 * Stored placements for `templateId` (optionally scoped to `appName`). The warm
 * chip uses each row's `uiTargets` as the selector-mode SetValue target for the
 * matching note section; the panel hydrates its verification view from them.
 */
export const getComputerUseNotePushTargets = (params: {
  templateId: string;
  appName?: string | null;
  workspaceId: string | null;
  /** Widen the read to structured rows. Off by default so `uiTargets`-only
   *  callers see byte-identical responses. */
  includeStructured?: boolean;
}): Promise<ComputerUseNotePushOutcome<ComputerUseNotePushTargetRow[]>> =>
  scopedRequest(params.workspaceId, 'GET', async (workspaceId, signal) => {
    const response = await getScribeHttpClients().scribeHttpV2.get<unknown>(
      NOTE_PUSH_TARGETS_PATH,
      {
        params: {
          template_id: params.templateId,
          ...(params.appName ? { app_name: params.appName } : {}),
          ...(params.includeStructured ? { include_structured: true } : {}),
        },
        signal,
        headers: workspaceHeaders(workspaceId),
      }
    );
    const rows = parseRows(response.data);
    if (rows === null) throw new Error('unexpected response shape');
    return rows;
  });

/**
 * Record the placements the warm chip verified on-device. Only verified
 * placements are sent (the endpoint rejects the rest); a call with nothing
 * verified is a no-op success. Never carries note content.
 */
export const recordComputerUseNotePushTargets = (input: {
  templateId: string;
  appName: string;
  placements: ComputerUseNotePushPlacement[];
  workspaceId: string | null;
}): Promise<ComputerUseNotePushOutcome<ComputerUseNotePushTargetRow[]>> => {
  const verified = input.placements.filter((placement) => placement.verified === true);
  if (input.workspaceId && !verified.length) {
    return Promise.resolve({ ok: true, value: [] });
  }
  return scopedRequest(input.workspaceId, 'POST', async (workspaceId, signal) => {
    const response = await getScribeHttpClients().scribeHttpV2.post<unknown>(
      NOTE_PUSH_TARGETS_PATH,
      {
        template_id: input.templateId,
        app_name: input.appName,
        placements: verified.map((placement) => ({
          section_heading: placement.sectionHeading,
          ui_targets: placement.uiTargets,
          verified: true,
        })),
      },
      { signal, headers: workspaceHeaders(workspaceId) }
    );
    const rows = parseRows(response.data);
    if (rows === null) throw new Error('unexpected response shape');
    return rows;
  });
};
